import { defineStore } from "pinia";
import RvApi from "@/rvapi";
import { BACKSIDE_IMAGE_PATH } from "@/constant";
import { getDefaultStampDateLabel, formatSwedishPostalCode } from "@/utils";
import type {
  BacksideFormat,
  BacksideSampleData,
  PdfPreviewTemplateData,
} from "@/types/backsideTemplate";

const defaultSampleData = (): BacksideSampleData => ({
  receiver: {
    name: "Anna Andersson",
    name2: "",
    address: "Storgatan 1",
    postalCode: "12345",
    city: "Stockholm",
    country: "Sweden",
  },
  date: getDefaultStampDateLabel(),
  trackingCode: "TDDTADDATTDADDADAFFTFFDAFTDDFAFAFFTDFATDDT",
  orderId: "ORD-000001",
  attribution: "Illustration: PostNord Frimarker",
  showCutMarks: false,
  prioritaire: false,
  sweden: true,
  branding: "",
});

export const useBacksideTemplateStore = defineStore("backsideTemplate", {
  state: () => ({
    previewPdfUrl: null as string | null,
    isGeneratingPreview: false,
    error: null as string | null,
    successMessage: null as string | null,
    sampleData: {
      a4: defaultSampleData(),
      a5: defaultSampleData(),
      a51: defaultSampleData(),
      a6: defaultSampleData(),
    } as Record<BacksideFormat, BacksideSampleData>,
  }),
  actions: {
    async generatePreview(
      format: BacksideFormat,
      rawTemplateHtml: string,
      stampPath?: string,
    ) {
      this.isGeneratingPreview = true;
      this.error = null;
      try {
        const data = this.sampleData[format];
        const templateData: PdfPreviewTemplateData = {
          backImagePath: BACKSIDE_IMAGE_PATH[format] ?? "",
          stamp: stampPath ?? "stamps/se_summer_landscape.png",
          date: data.date,
          trackingCode: data.trackingCode,
          orderId: data.orderId,
          attribution: data.attribution,
          prioritaire: data.prioritaire,
          sweden: data.sweden,
          showCutMarks: data.showCutMarks,
          receiver: {
            ...data.receiver,
            postalCode: formatSwedishPostalCode(
              data.receiver.postalCode,
              data.receiver.country,
            ),
          },
        };
        const uploadInfo = await RvApi.getPdfPreviewUploadUrl(format);
        await RvApi.uploadPdfPreviewTemplate(
          uploadInfo.uploadUrl,
          rawTemplateHtml,
          uploadInfo.contentType,
        );
        this.previewPdfUrl = await RvApi.generatePdfPreviewV2(
          format,
          uploadInfo.templateKey,
          templateData,
        );
      } catch (err: any) {
        this.error = err?.message ?? "Failed to generate preview.";
      } finally {
        this.isGeneratingPreview = false;
      }
    },
    clearMessages() {
      this.error = null;
      this.successMessage = null;
    },
    clearPreview() {
      if (this.previewPdfUrl) {
        URL.revokeObjectURL(this.previewPdfUrl);
        this.previewPdfUrl = null;
      }
    },
  },
});
